'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { factoryTourData } from '@/data/videoTour';
import { Play, X } from 'lucide-react';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export const FactoryTourSection: React.FC = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playBtnRef = useRef<HTMLButtonElement | null>(null);

  useGSAP(
    () => {
      if (!containerRef.current) return;

      // Reveal animation on scroll
      gsap.fromTo(
        containerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Continuous subtle pulse animation on the Play Button
      if (playBtnRef.current) {
        gsap.to(playBtnRef.current, {
          scale: 1.08,
          repeat: -1,
          yoyo: true,
          duration: 1.2,
          ease: 'power1.inOut',
        });
      }
    },
    { scope: containerRef }
  );

  return (
    <>
      <section
        ref={containerRef}
        aria-label="Factory Tour Video"
        className="w-full bg-white py-8 sm:py-12 md:py-16 px-3 xs:px-4 sm:px-6 flex justify-center"
      >
        <div className="w-full max-w-[1400px]">
          {/* Main Hero Card Container */}
          <div className="relative w-full h-[320px] xs:h-[380px] sm:h-[460px] md:h-[520px] rounded-2xl overflow-hidden shadow-xl border border-[#ECECEC] group cursor-pointer"
               onClick={() => setIsVideoOpen(true)}
          >
            {/* Background Factory Image */}
            <Image
              src={factoryTourData.posterImage}
              alt="Factory Tour"
              fill
              priority
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />

            {/* Dark Gradient Overlay for High Contrast Text */}
            <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/40 to-transparent z-10" />

            {/* Content Layer */}
            <div className="absolute inset-0 z-20 p-6 xs:p-8 sm:p-12 md:p-16 flex flex-col justify-between">
              {/* Top Text Content */}
              <div className="max-w-xl">
                <span className="text-[11px] xs:text-[13px] font-bold tracking-[0.2em] text-[#C89B3C] uppercase block mb-1">
                  {factoryTourData.title}
                </span>
                <h3 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-wide leading-tight drop-shadow-sm">
                  {factoryTourData.description}
                </h3>
              </div>

              {/* Bottom Play Button Overlay */}
              <div className="flex items-center gap-4">
                <button
                  ref={playBtnRef}
                  aria-label="Play Factory Tour Video"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsVideoOpen(true);
                  }}
                  className="relative w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 rounded-full border-2 border-white/80 bg-black/40 backdrop-blur-md flex items-center justify-center text-white transition-all duration-300 group-hover:bg-[#C89B3C] group-hover:border-[#C89B3C] shadow-2xl"
                >
                  <Play size={30} className="fill-white translate-x-0.5" />
                </button>

                <div className="flex flex-col text-white">
                  <span className="text-sm xs:text-base font-bold tracking-wider uppercase">
                    Watch Full Tour
                  </span>
                  <span className="text-[11px] xs:text-[12px] text-gray-300 font-normal">
                    HD Video • 2 Mins
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal Popup Overlay */}
      {isVideoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 xs:p-6"
          onClick={() => setIsVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-[#C89B3C] flex items-center justify-center transition-colors"
              aria-label="Close Video"
            >
              <X size={22} />
            </button>

            {/* Embed Video Iframe */}
            <iframe
              src={factoryTourData.videoUrl}
              title="Factory Tour Video"
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FactoryTourSection;